import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Cloud,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Database,
  Download,
  Eye,
  FileCode,
  FileText,
  Folder,
  FolderOpen,
  Image as ImageIcon,
  LogOut,
  MinusSquare,
  RefreshCw,
  Search,
  Square,
  Upload,
  X,
} from "lucide-react";
import { User } from "firebase/auth";
import { WorkspaceItem } from "../types";
import { downloadDriveFile } from "../lib/gdrive";

interface DriveWorkspaceProps {
  user: User | null;
  token: string | null;
  authLoading: boolean;
  onLogin: () => void;
  onLogout: () => void;
  items: WorkspaceItem[];
  driveLoading: boolean;
  onToggleCheck: (id: string) => void;
  onTogglePin: (id: string) => void;
  onUploadFile: (name: string, content: string, mimeType?: string) => void | Promise<void>;
  onFetchContent: (id: string) => Promise<string>;
  activeContextIds: string[];
  pinnedIds: string[];
  globalTheme?: "rodgerspc" | "light" | "dark";
  onRefreshDrive?: () => void;
  onAttachDriveFile?: (id: string) => void | Promise<void>;
  onDownloadDriveFile?: (item: WorkspaceItem) => void | Promise<void>;
  dbLoaded?: boolean;
  leftPanelView?: "drive" | "chats";
  onLeftPanelViewChange?: (view: "drive" | "chats") => void;
  savedChatCount?: number;
}

type IndexStatus = "idle" | "focused" | "loading" | "ready" | "failed";

const SOURCE_OF_TRUTH_NAMES = [
  "000 rodgerspc source of truth folder",
  "rodgerspc source of truth folder",
  "source of truth",
  "rodgerspc global source of truth",
];

function formatFileSize(size?: number): string {
  if (!size || size <= 0) return "—";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function normalizeDriveLabel(value: string): string {
  return value
    .toLowerCase()
    .replace(/[’‘]/g, "'")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isSourceOfTruth(item: WorkspaceItem): boolean {
  if (item.sourceOfTruth) return true;
  const name = normalizeDriveLabel(item.name || "");
  const path = normalizeDriveLabel(item.path || "");
  return SOURCE_OF_TRUTH_NAMES.some((needle) => {
    const normalizedNeedle = normalizeDriveLabel(needle);
    return name.includes(normalizedNeedle) || path.includes(normalizedNeedle);
  });
}

function isTextLikeFile(file: File): boolean {
  return (
    file.type.startsWith("text/") ||
    file.type.includes("json") ||
    file.type.includes("xml") ||
    /\.(txt|md|json|csv|log|html|htm|css|js|jsx|ts|tsx|py|sh|ps1|bat|cmd|reg|ini|yml|yaml)$/i.test(file.name)
  );
}

function isImageFile(item: WorkspaceItem): boolean {
  const content = item.content || "";
  return content.startsWith("data:image/") || /\.(png|jpe?g|gif|svg|webp)$/i.test(item.name || "");
}

function isPdfFile(item: WorkspaceItem): boolean {
  const content = item.content || "";
  return content.startsWith("data:application/pdf") || /\.pdf$/i.test(item.name || "");
}

function isBinaryFile(item: WorkspaceItem): boolean {
  const content = item.content || "";
  return (
    content.startsWith("data:application/vnd.openxmlformats-officedocument") ||
    content.startsWith("data:application/msword") ||
    content.startsWith("data:application/zip") ||
    /\.(docx|doc|xlsx|xls|zip|rar|7z|bin|exe|dll|msi)$/i.test(item.name || "")
  );
}

function walkItems(items: WorkspaceItem[], visitor: (item: WorkspaceItem) => void) {
  items.forEach((item) => {
    visitor(item);
    if (item.children && item.children.length > 0) walkItems(item.children, visitor);
  });
}

function countFocusedItems(items: WorkspaceItem[]): number {
  let count = 0;
  walkItems(items, (item) => {
    if (item.checked) count += 1;
  });
  return count;
}

function hasCheckedDescendant(item: WorkspaceItem): boolean {
  return Boolean(item.children?.some((child) => child.checked || hasCheckedDescendant(child)));
}

function areAllDescendantsChecked(item: WorkspaceItem): boolean {
  if (!item.children || item.children.length === 0) return Boolean(item.checked);
  return item.children.every((child) => Boolean(child.checked) && areAllDescendantsChecked(child));
}

function getFocusVisualState(item: WorkspaceItem): "checked" | "partial" | "unchecked" {
  if (item.checked && (item.type === "file" || areAllDescendantsChecked(item))) return "checked";
  if (item.checked || hasCheckedDescendant(item)) return "partial";
  return "unchecked";
}

function countAllNodes(items: WorkspaceItem[]): number {
  let count = 0;
  walkItems(items, () => {
    count += 1;
  });
  return count;
}

function getFileIcon(item: WorkspaceItem, checked: boolean) {
  if (isImageFile(item)) return <ImageIcon className={`w-3.5 h-3.5 shrink-0 ${checked ? "text-[#F6A400]" : "text-purple-400"}`} />;
  if (/\.(js|jsx|ts|tsx|json|html|css|ps1|bat|cmd|reg|py|sh|xml|yml|yaml)$/i.test(item.name || "")) {
    return <FileCode className={`w-3.5 h-3.5 shrink-0 ${checked ? "text-[#F6A400]" : "text-cyan-400"}`} />;
  }
  return <FileText className={`w-3.5 h-3.5 shrink-0 ${checked ? "text-[#F6A400]" : "text-slate-500"}`} />;
}

export default function DriveWorkspace({
  user,
  token,
  authLoading,
  onLogin,
  onLogout,
  items,
  driveLoading,
  onToggleCheck,
  onTogglePin,
  onUploadFile,
  onFetchContent,
  activeContextIds,
  pinnedIds,
  globalTheme = "rodgerspc",
  onRefreshDrive,
  onAttachDriveFile,
  onDownloadDriveFile,
  dbLoaded = true,
  leftPanelView = "drive",
  onLeftPanelViewChange,
  savedChatCount = 0,
}: DriveWorkspaceProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [collapsedFolders, setCollapsedFolders] = useState<Record<string, boolean>>({});
  const [dragOver, setDragOver] = useState(false);
  const [uploadingNames, setUploadingNames] = useState<string[]>([]);
  const [uploadNotice, setUploadNotice] = useState<{
    type: "success" | "error" | "working" | "info";
    message: string;
  } | null>(null);
  const [viewingFile, setViewingFile] = useState<WorkspaceItem | null>(null);
  const [fetchingViewId, setFetchingViewId] = useState<string | null>(null);
  const [previewItem, setPreviewItem] = useState<WorkspaceItem | null>(null);
  const [previewPos, setPreviewPos] = useState({ x: 0, y: 0 });
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; item: WorkspaceItem } | null>(null);
  const [localLoadedIds, setLocalLoadedIds] = useState<Set<string>>(() => new Set());
  const autoRefreshAttemptedRef = useRef(false);
  const defaultTreeViewSignatureRef = useRef("");

  const isLight = globalTheme === "light";
  const isDark = globalTheme === "dark";
  void onTogglePin;
  void activeContextIds;
  void pinnedIds;

  const focusedItemCount = useMemo(() => countFocusedItems(items), [items]);
  const totalNodeCount = useMemo(() => countAllNodes(items), [items]);

  useEffect(() => {
    setLocalLoadedIds((prev) => {
      const next = new Set(prev);
      let changed = false;

      walkItems(items, (item) => {
        if (item.type === "file" && (item.content || item.indexingStatus === "ready")) {
          if (!next.has(item.id)) {
            next.add(item.id);
            changed = true;
          }
        }
      });

      return changed ? next : prev;
    });
  }, [items]);

  useEffect(() => {
    if (!user || !token || !dbLoaded || driveLoading || !onRefreshDrive) return;
    if (items.length > 0) return;
    if (autoRefreshAttemptedRef.current) return;

    autoRefreshAttemptedRef.current = true;
    window.setTimeout(() => onRefreshDrive(), 250);
  }, [user, token, dbLoaded, driveLoading, items.length, onRefreshDrive]);

  useEffect(() => {
    if (items.length > 0) autoRefreshAttemptedRef.current = false;
  }, [items.length]);

  useEffect(() => {
    if (!uploadNotice || uploadNotice.type === "working") return;
    const timer = window.setTimeout(() => setUploadNotice(null), 5200);
    return () => window.clearTimeout(timer);
  }, [uploadNotice]);

  // Default view: show the master RodgersPC Command Center folder and its main subfolders only.
  // Do not open every nested folder on first load; that makes the Drive pane noisy.
  useEffect(() => {
    if (!items || items.length === 0) return;

    const signature = items
      .map((root) => `${root.id}:${(root.children || []).map((child) => child.id).join(",")}`)
      .join("|");

    if (defaultTreeViewSignatureRef.current === signature) return;
    defaultTreeViewSignatureRef.current = signature;

    const next: Record<string, boolean> = {};
    const walkForDefaultCollapse = (nodes: WorkspaceItem[], depth = 0) => {
      nodes.forEach((node) => {
        if (node.type === "folder") {
          next[node.id] = depth >= 1;
          if (node.children && node.children.length > 0) {
            walkForDefaultCollapse(node.children, depth + 1);
          }
        }
      });
    };

    walkForDefaultCollapse(items, 0);
    setCollapsedFolders(next);
  }, [items]);

  const shellClass = isLight
    ? "bg-[#F6F8FC] text-slate-900 border-slate-200"
    : isDark
      ? "bg-[#10121A] text-slate-100 border-neutral-800"
      : "bg-[#0B2F73] text-white border-[#F6A400]/25";

  const panelClass = isLight
    ? "bg-white/72 border-slate-200"
    : isDark
      ? "bg-[#161820]/80 border-neutral-800"
      : "bg-[#071A3D]/72 border-[#F6A400]/18";

  const darkerPanelClass = isLight
    ? "bg-white/90 border-slate-200"
    : isDark
      ? "bg-[#0B0D14]/90 border-neutral-800"
      : "bg-[#051126]/88 border-[#112240]";

  const mutedText = isLight ? "text-slate-500" : isDark ? "text-slate-400" : "text-[#D7DCE5]/75";
  const strongText = isLight ? "text-slate-900" : "text-white";
  const goldText = isLight ? "text-amber-700" : "text-[#F6A400]";
  const inputClass = isLight
    ? "bg-white/90 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-[#143F8F]"
    : isDark
      ? "bg-[#0B0D14] border-neutral-700 text-slate-100 placeholder:text-slate-500 focus:border-[#F6A400]"
      : "bg-[#071A3D] border-[#F6A400]/35 text-white placeholder:text-white/45 focus:border-[#F6A400]";

  const scopeCardClass = isLight
    ? "border-slate-200/80 bg-white/72 shadow-[0_10px_24px_rgba(15,23,42,0.06)]"
    : isDark
      ? "border-white/10 bg-white/[0.055] shadow-[0_12px_24px_rgba(0,0,0,0.18)]"
      : "border-[#F6A400]/18 bg-[#061A3B]/58 shadow-[0_14px_28px_rgba(0,0,0,0.14)]";

  const uploadCardClass = isLight
    ? "border-slate-300/80 bg-white/60 hover:border-[#143F8F]/35 hover:bg-white/78"
    : isDark
      ? "border-white/10 bg-white/[0.035] hover:border-[#F6A400]/25 hover:bg-white/[0.055]"
      : "border-[#F6A400]/16 bg-[#061A3B]/36 hover:border-[#F6A400]/34 hover:bg-[#061A3B]/54";

  const renderLeftPanelViewSwitch = () => {
    if (!onLeftPanelViewChange) return null;

    const baseIdle = isLight ? "text-slate-400 hover:text-slate-600" : "text-white/24 hover:text-white/52";
    const active = isLight ? "text-[#143F8F]/75" : "text-[#F6A400]/70";

    return (
      <div
        className="flex h-4 shrink-0 items-center gap-[2px] opacity-55 transition hover:opacity-95"
        id="drive-workspace-left-view-switch"
        title="Switch left panel between Drive and saved chats"
      >
        <button
          type="button"
          onClick={() => onLeftPanelViewChange("drive")}
          className={`text-[6px] font-semibold uppercase tracking-[0.10em] leading-none transition ${leftPanelView === "drive" ? active : baseIdle}`}
          aria-label="Show Drive panel"
        >
          Drive
        </button>
        <span className={isLight ? "text-[5px] text-slate-300" : "text-[5px] text-white/10"}>/</span>
        <button
          type="button"
          onClick={() => onLeftPanelViewChange("chats")}
          className={`text-[6px] font-semibold uppercase tracking-[0.10em] leading-none transition ${leftPanelView === "chats" ? active : baseIdle}`}
          aria-label="Show saved chats panel"
        >
          Chats
        </button>
        {savedChatCount > 0 && (
          <span className={isLight ? "ml-0.5 text-[5px] text-slate-400" : "ml-0.5 text-[5px] text-white/24"}>
            {savedChatCount}
          </span>
        )}
      </div>
    );
  };

  const refreshDrive = () => {
    autoRefreshAttemptedRef.current = false;
    onRefreshDrive?.();
  };

  useEffect(() => {
    if (!contextMenu) return;

    const close = () => setContextMenu(null);
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setContextMenu(null);
    };

    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [contextMenu]);

  const toggleFolder = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCollapsedFolders((prev) => ({ ...prev, [id]: !(prev[id] ?? false) }));
  };

  const expandAll = () => {
    const next: Record<string, boolean> = {};
    walkItems(items, (item) => {
      if (item.type === "folder") next[item.id] = false;
    });
    setCollapsedFolders(next);
  };

  const collapseAll = () => {
    const next: Record<string, boolean> = {};
    walkItems(items, (item) => {
      if (item.type === "folder") next[item.id] = true;
    });
    setCollapsedFolders(next);
  };

  const getNodeStatus = (item: WorkspaceItem): IndexStatus => {
    if (item.indexingStatus === "failed") return "failed";
    if (item.indexingStatus === "loading") return "loading";
    if (item.type === "file" && (item.content || item.indexingStatus === "ready" || localLoadedIds.has(item.id))) return "ready";
    if (item.checked || isSourceOfTruth(item)) return "focused";
    return "idle";
  };

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    const filter = (nodes: WorkspaceItem[]): WorkspaceItem[] => {
      return nodes
        .map((item) => {
          const name = (item.name || "").toLowerCase();
          const path = (item.path || "").toLowerCase();
          const content = (item.content || "").toLowerCase();
          const selfMatchesSearch = !query || name.includes(query) || path.includes(query) || content.includes(query);

          if (item.type === "folder") {
            const children = filter(item.children || []);
            if (selfMatchesSearch || children.length > 0) return { ...item, children };
            return null;
          }

          return selfMatchesSearch ? item : null;
        })
        .filter((item): item is WorkspaceItem => item !== null);
    };

    return filter(items);
  }, [items, searchQuery]);


  const readUploadFilePayload = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        const payload = String(event.target?.result || "");
        if (!payload) {
          reject(new Error(`File read produced an empty payload: ${file.name}`));
          return;
        }

        resolve(payload);
      };

      reader.onerror = () => {
        reject(new Error(`Could not read file before Drive upload: ${file.name}`));
      };

      if (isTextLikeFile(file)) reader.readAsText(file);
      else reader.readAsDataURL(file);
    });
  };

  const handleFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    setUploadingNames(fileArray.map((file) => file.name));
    setUploadNotice({
      type: "working",
      message: `Uploading ${fileArray.length} file${fileArray.length === 1 ? "" : "s"} to the RodgersPC Command Center Drive folder...`,
    });

    const failed: string[] = [];
    const uploaded: string[] = [];

    for (const file of fileArray) {
      try {
        const payload = await readUploadFilePayload(file);
        await Promise.resolve(
          onUploadFile(file.name, payload, file.type || "application/octet-stream"),
        );
        uploaded.push(file.name);
      } catch (err) {
        console.error("[Drive Workspace] Upload failed:", file.name, err);
        failed.push(file.name);
      }
    }

    if (failed.length > 0) {
      setUploadNotice({
        type: "error",
        message: `Upload failed for ${failed.join(", ")}. Nothing was marked as a real Drive upload for those file${failed.length === 1 ? "" : "s"}.`,
      });
    } else {
      setUploadNotice({
        type: "success",
        message: `Drive upload completed for ${uploaded.join(", ")}. Refreshing tree and verifying Drive item visibility.`,
      });
    }

    window.setTimeout(() => setUploadingNames([]), 1600);
  };

  const fetchPreviewContentWithTimeout = async (id: string, timeoutMs = 12000): Promise<string> => {
    let timeoutId: number | undefined;

    try {
      return await Promise.race([
        onFetchContent(id),
        new Promise<string>((_, reject) => {
          timeoutId = window.setTimeout(() => {
            reject(new Error("Preview timed out. Use Download for the original Drive file, or resync Drive and try again."));
          }, timeoutMs);
        }),
      ]);
    } finally {
      if (timeoutId) window.clearTimeout(timeoutId);
    }
  };

  const handleViewFile = async (item: WorkspaceItem) => {
    if (!item || item.type !== "file" || !item.id) {
      setUploadNotice({ type: "error", message: "Preview failed: invalid Drive file selection." });
      return;
    }

    if (item.content) {
      setLocalLoadedIds((prev) => new Set(prev).add(item.id));
      setViewingFile(item);
      return;
    }

    setFetchingViewId(item.id);
    setViewingFile({ ...item, content: "" });

    try {
      const content = await fetchPreviewContentWithTimeout(item.id);
      const previewContent =
        content && content.trim()
          ? content
          : "[File preview unavailable: Drive returned no readable preview content for this file. Use Download for the original file.]";

      setLocalLoadedIds((prev) => new Set(prev).add(item.id));
      setViewingFile((current) => (current?.id === item.id ? { ...item, content: previewContent } : current));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setViewingFile((current) =>
        current?.id === item.id
          ? { ...item, content: `[File preview failed: ${message}]` }
          : current,
      );
      setUploadNotice({ type: "error", message: `Preview failed: ${message}` });
    } finally {
      setFetchingViewId((current) => (current === item.id ? null : current));
    }
  };

  const closeContextMenu = () => setContextMenu(null);

  const openContextMenu = (event: React.MouseEvent, item: WorkspaceItem) => {
    event.preventDefault();
    event.stopPropagation();
    if (!item || !item.id) return;

    setPreviewItem(null);
    const menuWidth = 196;
    const menuHeight = item.type === "folder" ? 126 : 174;
    const x = Math.max(8, Math.min(event.clientX, window.innerWidth - menuWidth - 12));
    const y = Math.max(8, Math.min(event.clientY, window.innerHeight - menuHeight - 12));
    setContextMenu({ x, y, item });
  };

  const downloadDriveItem = async (item: WorkspaceItem) => {
    if (!item || item.type !== "file") {
      throw new Error("Only files can be downloaded from the Drive tree.");
    }

    if (onDownloadDriveFile) {
      await onDownloadDriveFile(item);
      return;
    }

    if (token) {
      await downloadDriveFile(token, item.id, item.name || "RodgersPC_Drive_File");
      return;
    }

    let content = item.content;
    if (!content) {
      content = await onFetchContent(item.id);
    }

    let blob: Blob;
    if (content.startsWith("data:")) {
      blob = await fetch(content).then((response) => response.blob());
    } else {
      blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = item.name || "RodgersPC_Drive_File";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const attachDriveItem = async (item: WorkspaceItem) => {
    if (!item || item.type !== "file") {
      throw new Error("Only files can be loaded into the active chat from this menu.");
    }

    if (onAttachDriveFile) {
      await onAttachDriveFile(item.id);
      setLocalLoadedIds((prev) => new Set(prev).add(item.id));
      setUploadNotice({
        type: "success",
        message: `Loaded into chat: ${item.name || "Drive file"}`,
      });
      return;
    }

    if (!item.checked) {
      onToggleCheck(item.id);
    }

    if (!item.content) {
      try {
        const content = await fetchPreviewContentWithTimeout(item.id);
        if (content && content.trim()) {
          setLocalLoadedIds((prev) => new Set(prev).add(item.id));
        }
      } catch {
        // Focus still succeeded; App.tsx must wire onAttachDriveFile for true chat insertion.
      }
    } else {
      setLocalLoadedIds((prev) => new Set(prev).add(item.id));
    }

    setUploadNotice({
      type: "info",
      message: `Focused file for chat: ${item.name || "Drive file"}. App.tsx must pass onAttachDriveFile for visible chat insertion.`,
    });
  };


  const renderStatusBadge = (status: IndexStatus, item: WorkspaceItem) => {
    if (status === "loading") {
      return (
        <span className="inline-flex items-center gap-1 rounded bg-[#F6A400]/10 px-1 py-0.5 text-[8px] font-bold font-mono text-[#F6A400]">
          <RefreshCw className="w-2.5 h-2.5 animate-spin" />
          Loading
        </span>
      );
    }

    if (status === "ready") {
      return (
        <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-1 py-0.5 text-[8px] font-bold font-mono text-emerald-400">
          <CheckCircle2 className="w-2.5 h-2.5" />
          Loaded
        </span>
      );
    }

    if (status === "failed") {
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleCheck(item.id);
          }}
          className="rounded bg-red-500/10 px-1 py-0.5 text-[8px] font-bold font-mono text-red-400 hover:bg-red-500/20"
          title="Drive content failed. Click to toggle focus and retry if needed."
        >
          Failed
        </button>
      );
    }

    return null;
  };

  const renderCheckboxIcon = (state: "checked" | "partial" | "unchecked") => {
    if (state === "checked") return <CheckSquare className="w-3.5 h-3.5 text-[#F6A400]" />;
    if (state === "partial") return <MinusSquare className="w-3.5 h-3.5 text-[#F6A400]" />;
    return <Square className="w-3.5 h-3.5" />;
  };

  const renderTree = (tree: WorkspaceItem[], depth = 0): React.ReactNode => {
    return tree.map((item) => {
      const isFolder = item.type === "folder";
      const focusState = getFocusVisualState(item);
      const checked = focusState === "checked";
      const partial = focusState === "partial";
      const focused = checked || partial;
      const status = getNodeStatus(item);
      const sot = isSourceOfTruth(item);
      const collapsed = collapsedFolders[item.id] ?? false;
      const childCount = item.children?.length || 0;

      if (isFolder) {
        return (
          <div key={item.id} className="select-none">
            <div
              className={`group flex items-center justify-between rounded-xl px-2 py-1.5 transition-colors cursor-pointer ${
                focused || sot
                  ? isLight
                    ? "bg-amber-500/10 text-amber-900"
                    : "bg-[#F6A400]/10 text-[#FFB72B]"
                  : isLight
                    ? "text-slate-800 hover:bg-slate-200/70"
                    : "text-slate-300 hover:bg-[#112240]/60"
              }`}
              style={{ paddingLeft: `${8 + depth * 12}px` }}
              aria-label={item.path || item.name}
              onClick={(e) => toggleFolder(item.id, e)}
              onContextMenu={(e) => openContextMenu(e, item)}
              id={`folder-item-${item.id}`}
            >
              <div className="flex min-w-0 flex-1 items-center gap-1.5">
                <span className="shrink-0 text-slate-500 group-hover:text-[#F6A400]">
                  {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </span>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleCheck(item.id);
                  }}
                  className="shrink-0 text-slate-500 hover:text-[#F6A400]"
                  aria-label={focused ? "Remove this folder and everything inside it from focused Drive data" : "Focus this folder and everything inside it"}
                >
                  {renderCheckboxIcon(focusState)}
                </button>

                <Folder className={`w-3.5 h-3.5 shrink-0 ${focused || sot ? "text-[#F6A400]" : "text-sky-400"}`} />

                <span className="truncate text-[11px] font-semibold leading-4">
                  {item.name}
                </span>

                {sot && (
                  <span className="rounded-full bg-[#F6A400]/14 px-1.5 py-0.5 text-[7.5px] font-bold uppercase text-[#F6A400]">
                    Source
                  </span>
                )}

                {renderStatusBadge(status, item)}
              </div>

              <span className={`ml-2 shrink-0 text-[9px] font-mono ${mutedText}`}>{childCount}</span>
            </div>

            {!collapsed && item.children && item.children.length > 0 && <div className="mt-0.5 space-y-0.5">{renderTree(item.children, depth + 1)}</div>}
          </div>
        );
      }

      return (
        <div
          key={item.id}
          className={`group flex items-center justify-between rounded-xl pr-2 py-1 transition-colors cursor-pointer ${
            checked
              ? isLight
                ? "bg-amber-500/10 text-slate-900 border-l-2 border-amber-600"
                : "bg-[#F6A400]/10 text-slate-100 border-l-2 border-[#F6A400]"
              : isLight
                ? "text-slate-800 hover:bg-slate-200/70"
                : "text-slate-300 hover:bg-[#112240]/60"
          }`}
          style={{ paddingLeft: `${28 + depth * 12}px` }}
          aria-label={item.path || item.name}
          onContextMenu={(e) => openContextMenu(e, item)}
          id={`file-item-${item.id}`}
        >
          <div
            className="flex min-w-0 flex-1 items-center gap-1.5 py-1"
            onClick={() => onToggleCheck(item.id)}
            onMouseEnter={(e) => {
              if (item.content && !item.content.startsWith("data:")) {
                const bounds = e.currentTarget.getBoundingClientRect();
                setPreviewItem(item);
                setPreviewPos({ x: bounds.right + 10, y: bounds.top - 8 });
              }
            }}
            onMouseLeave={() => setPreviewItem(null)}
          >
            <button type="button" className="shrink-0 text-slate-500 hover:text-[#F6A400]" aria-label={checked ? "Remove this file from focused Drive data" : "Focus this file"}>
              {renderCheckboxIcon(focusState)}
            </button>

            {getFileIcon(item, checked)}

            <span className={`truncate text-[10.5px] font-mono leading-4 ${checked ? goldText : strongText}`}>
              {item.name}
            </span>

            {renderStatusBadge(status, item)}
          </div>

          <div className="flex shrink-0 items-center gap-1 opacity-45 transition-opacity group-hover:opacity-100">
            <span className={`hidden text-[8px] font-mono xl:inline ${mutedText}`}>{formatFileSize(item.size)}</span>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleViewFile(item);
              }}
              className={`rounded p-1 ${isLight ? "hover:bg-slate-200 hover:text-amber-700" : "hover:bg-[#112240] hover:text-[#F6A400]"}`}
              aria-label="Open file preview"
            >
              {fetchingViewId === item.id ? <RefreshCw className="w-3 h-3 animate-spin text-[#F6A400]" /> : <Eye className="w-3 h-3" />}
            </button>
          </div>
        </div>
      );
    });
  };

  const renderFilePreview = (item: WorkspaceItem) => {
    const content = item.content || "";

    if (!content) {
      return (
        <div className="flex flex-col items-center justify-center rounded-xl border border-[#112240] bg-[#020b1e]/60 px-6 py-12 text-center">
          <RefreshCw className="mb-3 h-6 w-6 animate-spin text-[#F6A400]" />
          <p className="text-xs font-mono text-slate-400">Loading file content...</p>
        </div>
      );
    }

    if (isImageFile(item)) {
      return (
        <div className="flex flex-col items-center justify-center overflow-hidden rounded-xl border border-[#112240] bg-[#020b1e]/60 py-6">
          <img src={content} className="max-h-96 rounded-lg border border-[#112240] object-contain shadow-xl" alt={item.name} />
          <div className="mt-3 text-[10px] font-mono text-slate-500">Image file | {formatFileSize(item.size)}</div>
        </div>
      );
    }

    if (isPdfFile(item)) {
      return (
        <div className="flex flex-col items-center justify-center overflow-hidden rounded-xl border border-[#112240] bg-[#020b1e]/60 py-4">
          <iframe src={content} className="h-[500px] w-full rounded-lg border-0 bg-white shadow-xl" title={item.name} />
          <div className="mt-3 text-[10px] font-mono text-slate-500">PDF file | {formatFileSize(item.size)}</div>
        </div>
      );
    }

    if (isBinaryFile(item)) {
      return (
        <div className="flex flex-col items-center justify-center space-y-4 rounded-xl border border-[#112240] bg-[#020b1e]/60 px-6 py-12 text-center">
          <FileCode className="h-8 w-8 text-[#F6A400]" />
          <div>
            <h4 className="text-sm font-bold font-mono text-slate-200">{item.name}</h4>
            <p className="mt-1 text-[10px] font-mono text-slate-500">Binary preview is limited. This file can still stay focused as Drive metadata.</p>
          </div>
          {content.startsWith("data:") && (
            <a href={content} download={item.name} className="rounded-lg border border-[#F6A400]/45 bg-[#F6A400]/10 px-4 py-2 text-xs font-black uppercase tracking-wider text-[#F6A400] hover:bg-[#F6A400]/20">
              Download Local Copy
            </a>
          )}
        </div>
      );
    }

    const isCsv = /\.csv$/i.test(item.name || "") || (content.includes(",") && content.includes("\n") && !content.trim().startsWith("{"));
    if (isCsv) {
      const rows = content
        .split("\n")
        .map((line) => line.split(","))
        .filter((row) => row.join("").trim() !== "");

      if (rows.length > 0) {
        const headers = rows[0];
        const body = rows.slice(1, 250);
        return (
          <div className="max-h-[500px] overflow-x-auto rounded-xl border border-[#112240] bg-[#020b1e]">
            <table className="w-full border-collapse text-left text-[11px] font-mono">
              <thead>
                <tr className="sticky top-0 border-b border-[#112240] bg-[#051126]/95 text-[#F6A400]">
                  {headers.map((header, index) => (
                    <th key={index} className="px-3 py-2 font-bold">
                      {header.replace(/[\"']/g, "")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#112240]">
                {body.map((row, rowIndex) => (
                  <tr key={rowIndex} className="text-slate-300 hover:bg-slate-800/20">
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex} className="max-w-xs truncate px-3 py-1.5">
                        {cell.replace(/[\"']/g, "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 251 && <div className="border-t border-[#112240] p-2 text-[10px] font-mono text-slate-500">Showing first 250 rows.</div>}
          </div>
        );
      }
    }

    return (
      <div className="max-h-[520px] overflow-auto rounded-xl border border-[#112240] bg-[#020b1e] p-3.5 select-text">
        <pre className="whitespace-pre-wrap text-xs leading-relaxed text-slate-300 font-mono">{content}</pre>
      </div>
    );
  };

  if (authLoading) {
    return (
      <div className={`flex h-full flex-col items-center justify-center border-r p-6 ${shellClass}`}>
        <RefreshCw className="h-6 w-6 animate-spin text-[#F6A400]" />
        <span className={`mt-2 text-xs font-mono ${mutedText}`}>Connecting secure gateway...</span>
      </div>
    );
  }

  if (user && token && !dbLoaded) {
    return (
      <div className={`flex h-full flex-col items-center justify-center border-r p-6 ${shellClass}`}>
        <RefreshCw className="h-6 w-6 animate-spin text-[#F6A400]" />
        <span className={`mt-2 animate-pulse text-xs font-mono ${mutedText}`}>Restoring workspace state...</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={`flex h-full flex-col justify-center border-r p-5 font-sans ${shellClass}`}>
        <div className="space-y-4 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-[#F6A400]/30 bg-[#F6A400]/10 text-[#F6A400]">
            <FolderOpen className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xs font-black uppercase tracking-widest">Activate Drive Workspace</h2>
            <p className={`mt-2 text-[11px] leading-relaxed font-mono ${mutedText}`}>Sign in to load the RodgersPC Command Folder tree. Checked/gold files and folders become focused RodgersPC Drive data.</p>
          </div>
          <button type="button" onClick={onLogin} className="mx-auto rounded-lg border border-[#F6A400]/40 bg-[#F6A400]/15 px-4 py-2 text-[11px] font-black uppercase tracking-wider text-[#F6A400] hover:bg-[#F6A400]/25">
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex h-full flex-col border-r font-sans ${shellClass}`} id="drive-workspace-panel" data-phase12="drive-context-menu-load-chat-v1">
      <div className={`shrink-0 border-b px-3 py-3 ${panelClass}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${driveLoading ? "animate-pulse bg-[#F6A400]" : "bg-emerald-400"}`} />
              <h2 className="truncate text-[12px] font-semibold tracking-[-0.01em]">
                Drive Brain
              </h2>
            </div>
            <p className={`mt-0.5 truncate text-[10px] ${mutedText}`}>
              {user.email}
            </p>
          </div>

          <div className="flex items-center gap-1">
            {onRefreshDrive && token && (
              <button
                type="button"
                onClick={refreshDrive}
                disabled={driveLoading}
                title="Resync Drive"
                className={`inline-flex h-8 w-8 items-center justify-center rounded-xl border transition ${
                  isLight
                    ? "border-slate-200 bg-white text-slate-500 hover:border-[#143F8F]/30 hover:text-[#143F8F]"
                    : "border-white/10 bg-white/[0.04] text-slate-400 hover:border-[#F6A400]/35 hover:text-[#F6A400]"
                } disabled:cursor-wait disabled:opacity-60`}
                id="google-refresh-btn"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${driveLoading ? "animate-spin" : ""}`} />
              </button>
            )}

            <button
              type="button"
              onClick={onLogout}
              title="Sign out"
              className={`inline-flex h-8 w-8 items-center justify-center rounded-xl border transition ${
                isLight
                  ? "border-slate-200 bg-white text-slate-500 hover:border-rose-200 hover:text-rose-600"
                  : "border-white/10 bg-white/[0.04] text-slate-400 hover:border-rose-400/30 hover:text-rose-300"
              }`}
              id="google-signout-btn"
            >
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className={`mt-3 rounded-2xl border px-3 py-2.5 backdrop-blur-xl ${scopeCardClass}`} data-phase6="drive-brain-scope-polish-v6">
          <div className="flex items-center justify-between gap-2">
            <span className={`text-[9px] font-black uppercase tracking-[0.16em] ${isLight ? "text-[#143F8F]" : "text-[#F6A400]"}`}>
              Drive Brain
            </span>
            <span className={`truncate text-[10px] font-semibold ${strongText}`}>
              {focusedItemCount} selected
            </span>
          </div>
          <div className={`mt-1 flex items-center justify-between gap-2 text-[10px] leading-snug ${mutedText}`}>
            <span>{totalNodeCount} total</span>
            {driveLoading && <span className="text-[#F6A400]">Syncing</span>}
          </div>
        </div>
      </div>

      <div className={`shrink-0 border-b px-3 py-2.5 ${panelClass}`}>
        <div className="relative">
          <Search className={`absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 ${mutedText}`} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search Drive..."
            className={`rpc-drive-search h-9 w-full rounded-2xl border py-1 pl-9 pr-8 text-[12px] outline-none transition ${inputClass}`}
            id="workspace-search-bar"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-rose-400"
              title="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="mt-2 flex w-full items-center justify-between gap-2" data-phase6="drive-shop-memory-controls-v6">
          {renderLeftPanelViewSwitch() || <span />}
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={expandAll}
              className={`inline-flex h-7 w-7 items-center justify-center rounded-lg border text-[12px] font-bold transition ${
                isLight
                  ? "border-slate-200 bg-white text-slate-500 hover:text-[#143F8F]"
                  : "border-white/10 bg-white/[0.04] text-slate-400 hover:text-[#F6A400]"
              }`}
              title="Expand all Drive folders"
              aria-label="Expand all Drive folders"
            >
              +
            </button>
            <button
              type="button"
              onClick={collapseAll}
              className={`inline-flex h-7 w-7 items-center justify-center rounded-lg border text-[12px] font-bold transition ${
                isLight
                  ? "border-slate-200 bg-white text-slate-500 hover:text-[#143F8F]"
                  : "border-white/10 bg-white/[0.04] text-slate-400 hover:text-[#F6A400]"
              }`}
              title="Collapse all Drive folders"
              aria-label="Collapse all Drive folders"
            >
              −
            </button>
          </div>
        </div>
      </div>

      <div className={`relative flex-1 overflow-y-auto border-b p-2.5 ${isLight ? "border-slate-200" : "border-white/10"}`} id="file-tree-container">
        {filteredItems.length === 0 ? (
          driveLoading ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <RefreshCw className="mb-2 h-5 w-5 animate-spin text-[#F6A400]" />
              <p className={`text-xs font-mono ${mutedText}`}>Syncing RodgersPC Drive Folder...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
              <Database className={`mb-2 h-5 w-5 ${mutedText}`} />
              <p className={`text-xs leading-relaxed font-mono ${mutedText}`}>Drive tree is empty or not loaded yet. Refresh Drive before relying on Drive focus.</p>
              {token && onRefreshDrive && (
                <button type="button" onClick={refreshDrive} className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-[#F6A400]/30 bg-[#F6A400]/10 px-3 py-1.5 text-[10px] font-black uppercase text-[#F6A400] hover:bg-[#F6A400]/18">
                  <RefreshCw className="h-3 w-3" /> Refresh Drive
                </button>
              )}
            </div>
          )
        ) : (
          <>
            {driveLoading && (
              <div className="sticky top-1 float-right z-35 mr-1 flex items-center gap-1 rounded-md border border-[#D7DCE5]/10 bg-[#051126]/95 px-2 py-1 text-[8.5px] font-mono text-[#F6A400] shadow-lg">
                <RefreshCw className="h-2.5 w-2.5 animate-spin" />
                <span>Syncing...</span>
              </div>
            )}
            <div className="space-y-0.5">{renderTree(filteredItems)}</div>
          </>
        )}
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={async (e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files?.length) await handleFiles(e.dataTransfer.files);
        }}
        className={`shrink-0 p-3 transition-colors ${dragOver ? "bg-[#F6A400]/10" : isLight ? "bg-white/58" : "bg-black/8"}`}
      >
        <label className={`flex cursor-pointer select-none flex-col items-center justify-center rounded-2xl border border-dashed p-3 transition-colors backdrop-blur-xl ${dragOver ? "border-[#F6A400] bg-[#F6A400]/10" : uploadCardClass}`}>
          <Upload className={`mb-0.5 h-4 w-4 ${dragOver ? "animate-bounce text-[#F6A400]" : "text-slate-500"}`} />
          <span className={`text-[11px] font-bold ${isLight ? "text-slate-700" : "text-slate-200"}`}>Upload to Drive</span>
          <span className={`mt-0.5 max-w-[210px] text-center text-[9.5px] leading-snug ${mutedText}`}>Upload files to RodgersPC Drive</span>
          <input type="file" multiple accept="*" onChange={async (e) => {
            if (e.target.files?.length) await handleFiles(e.target.files);
            e.currentTarget.value = "";
          }} className="hidden" />
        </label>
        {uploadingNames.length > 0 && <div className="mt-2 truncate text-[9px] font-mono text-[#F6A400]">Processing: {uploadingNames.join(", ")}</div>}
        {uploadNotice && (
          <div
            className={`mt-2 flex items-start gap-1.5 rounded-md border px-2 py-1.5 text-[9px] font-mono leading-snug ${
              uploadNotice.type === "success"
                ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                : uploadNotice.type === "error"
                  ? "border-rose-500/35 bg-rose-500/10 text-rose-300"
                  : "border-[#F6A400]/30 bg-[#F6A400]/10 text-[#F6A400]"
            }`}
          >
            {uploadNotice.type === "error" ? (
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
            ) : uploadNotice.type === "success" ? (
              <Cloud className="mt-0.5 h-3 w-3 shrink-0" />
            ) : (
              <RefreshCw className="mt-0.5 h-3 w-3 shrink-0 animate-spin" />
            )}
            <span>{uploadNotice.message}</span>
          </div>
        )}
      </div>


      {contextMenu?.item && (
        <div
          className={`fixed z-55 w-48 overflow-hidden rounded-xl border p-1 text-[11px] shadow-2xl backdrop-blur-xl ${
            isLight
              ? "border-slate-200 bg-white/95 text-slate-800"
              : "border-white/10 bg-[#071A3D]/96 text-slate-200"
          }`}
          style={{ left: `${contextMenu.x}px`, top: `${contextMenu.y}px` }}
          onClick={(event) => event.stopPropagation()}
          onMouseLeave={closeContextMenu}
          id="drive-tree-context-menu"
        >
          {contextMenu.item.type === "file" ? (
            <>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await attachDriveItem(contextMenu.item);
                  } catch (err) {
                    setUploadNotice({
                      type: "error",
                      message: `Could not load file into chat: ${err instanceof Error ? err.message : String(err)}`,
                    });
                  } finally {
                    closeContextMenu();
                  }
                }}
                className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left font-semibold transition ${isLight ? "hover:bg-slate-100" : "hover:bg-white/[0.08]"}`}
              >
                <CheckSquare className="h-3.5 w-3.5 text-[#F6A400]" />
                Load into chat
              </button>

              <button
                type="button"
                onClick={() => {
                  try {
                    handleViewFile(contextMenu.item);
                  } catch (err) {
                    setUploadNotice({
                      type: "error",
                      message: `Could not preview file: ${err instanceof Error ? err.message : String(err)}`,
                    });
                  } finally {
                    closeContextMenu();
                  }
                }}
                className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left font-semibold transition ${isLight ? "hover:bg-slate-100" : "hover:bg-white/[0.08]"}`}
              >
                <Eye className="h-3.5 w-3.5 text-sky-400" />
                Preview
              </button>

              <button
                type="button"
                onClick={async () => {
                  try {
                    await downloadDriveItem(contextMenu.item);
                  } catch (err) {
                    setUploadNotice({
                      type: "error",
                      message: `Download failed: ${err instanceof Error ? err.message : String(err)}`,
                    });
                  } finally {
                    closeContextMenu();
                  }
                }}
                className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left font-semibold transition ${isLight ? "hover:bg-slate-100" : "hover:bg-white/[0.08]"}`}
              >
                <Download className="h-3.5 w-3.5 text-emerald-400" />
                Download
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => {
                  onToggleCheck(contextMenu.item.id);
                  closeContextMenu();
                }}
                className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left font-semibold transition ${isLight ? "hover:bg-slate-100" : "hover:bg-white/[0.08]"}`}
              >
                <CheckSquare className="h-3.5 w-3.5 text-[#F6A400]" />
                {getFocusVisualState(contextMenu.item) === "unchecked" ? "Select folder" : "Clear folder"}
              </button>

              {onRefreshDrive && (
                <button
                  type="button"
                  onClick={() => {
                    refreshDrive();
                    closeContextMenu();
                  }}
                  className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left font-semibold transition ${isLight ? "hover:bg-slate-100" : "hover:bg-white/[0.08]"}`}
                >
                  <RefreshCw className="h-3.5 w-3.5 text-sky-400" />
                  Resync Drive
                </button>
              )}
            </>
          )}
        </div>
      )}

      {viewingFile && (
        <div className="fixed inset-0 z-55 flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm animate-fade-in" id="structured-file-viewer-modal">
          <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-[#112240] bg-[#051126] shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b border-[#112240] bg-[#020b1e]/90 px-4 py-3">
              <div className="flex min-w-0 items-center gap-2">
                {isImageFile(viewingFile) ? <ImageIcon className="h-4 w-4 shrink-0 text-[#F6A400]" /> : <FileCode className="h-4 w-4 shrink-0 text-[#F6A400]" />}
                <div className="min-w-0">
                  <h3 className="truncate text-xs font-black uppercase tracking-widest text-[#F6A400] font-mono">{viewingFile.name}</h3>
                  <p className="truncate text-[9px] text-[#D7DCE5]/45 font-mono">{viewingFile.path}</p>
                </div>
              </div>
              <button type="button" onClick={() => setViewingFile(null)} className="shrink-0 rounded-lg bg-[#122444] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:text-white">
                Close
              </button>
            </div>
            <div className="flex-1 overflow-y-auto bg-[#051126] p-4">{renderFilePreview(viewingFile)}</div>
            <div className="flex items-center justify-between gap-3 border-t border-[#112240] bg-[#020b1e]/60 px-4 py-2 text-[11px] text-slate-500 font-mono">
              <span className="truncate">Path: {viewingFile.path}</span>
              <span className="shrink-0">Size: {formatFileSize(viewingFile.size)}</span>
            </div>
          </div>
        </div>
      )}

      {previewItem && previewItem.content && !viewingFile && (
        <div
          className="pointer-events-none fixed z-50 max-w-md rounded-xl border border-[#112240] bg-[#020b1e]/95 p-3 shadow-2xl backdrop-blur-md"
          style={{ left: `${previewPos.x}px`, top: `${Math.min(previewPos.y, window.innerHeight - 250)}px` }}
          id="workspace-file-preview-hover"
        >
          <div className="mb-2 flex items-center gap-2 border-b border-[#112240] pb-1.5">
            <FileCode className="h-3.5 w-3.5 text-[#F6A400]" />
            <span className="truncate text-xs font-bold text-[#F6A400] font-mono">{previewItem.name}</span>
            <span className="ml-auto text-[9px] text-slate-500 font-mono">{formatFileSize(previewItem.size)}</span>
          </div>
          <div className="line-clamp-[8] select-none whitespace-pre-wrap text-[10px] leading-relaxed text-slate-400 font-mono">{previewItem.content}</div>
        </div>
      )}
    </div>
  );
}